<script setup>
import {useNotification} from '@kyvg/vue3-notification'
import {computed, defineEmits, defineExpose, defineProps, onMounted, ref} from 'vue'
import {Modal as BaseModal} from 'bootstrap'
import ApiErrors from '@shared-components/ui/ApiErrors.vue'
import BaseForm from '@shared-components/base/BaseForm.vue'
import TheSelect from '@shared-components/ui/selects/TheSelect.vue'
import {useAdAccountWhiteListRequestStore} from '@/views/ad-account-requests/white-lists/AdAccountWhiteListRequestStore'
import MetaDatingWhiteListForm from '@shared-components/pages/requests/white-list/forms/meta/MetaDatingWhiteListForm.vue'
import MetaGamblingWhiteListForm from '@shared-components/pages/requests/white-list/forms/meta/MetaGamblingWhiteListForm.vue'
import MetaPharmaWhiteListForm from '@shared-components/pages/requests/white-list/forms/meta/MetaPharmaWhiteListForm.vue'
import MetaCryptoWhiteListForm from '@shared-components/pages/requests/white-list/forms/meta/MetaCryptoWhiteListForm.vue'

import TheSelectAdAccounts from '@shared-components/ui/selects/TheSelectAdAccounts.vue'
import {storeToRefs} from 'pinia'
import {useAuthenticationStore, useDefaultStore} from '@/stores'
import {useCompanyStore} from '@/views/companies/CompanyStore'
import GoogleGamblingWhiteListForm from '@shared-components/pages/requests/white-list/forms/google/GoogleGamblingWhiteListForm.vue'
import GoogleSocialCasinoWhiteListForm from '@shared-components/pages/requests/white-list/forms/google/GoogleSocialCasinoWhiteListForm.vue'
import GoogleFinancialWhiteListForm from '@shared-components/pages/requests/white-list/forms/google/GoogleFinancialWhiteListForm.vue'
import ProcessOwnerRow from '@/views/tasks/components/ProcessOwnerRow.vue'
import GoogleHealthInsuranceWhiteListForm from '@shared-components/pages/requests/white-list/forms/google/GoogleHealthInsuranceWhiteListForm.vue'
import GoogleDatingWhiteListForm from '@shared-components/pages/requests/white-list/forms/google/GoogleDatingWhiteListForm.vue'
import SnapchatGamblingWhiteListForm from '@shared-components/pages/requests/white-list/forms/snapchat/SnapchatGamblingWhiteListForm.vue'
import SnapchatPharmaWhiteListForm from '@shared-components/pages/requests/white-list/forms/snapchat/SnapchatPharmaWhiteListForm.vue'
import SnapchatFinancialWhiteListForm from '@shared-components/pages/requests/white-list/forms/snapchat/SnapchatFinancialWhiteListForm.vue'
import BingGamblingWhiteListForm from '@shared-components/pages/requests/white-list/forms/bing/BingGamblingWhiteListForm.vue'
import BingCryptoWhiteListForm from '@shared-components/pages/requests/white-list/forms/bing/BingCryptoWhiteListForm.vue'
import BingDatingWhiteListForm from '@shared-components/pages/requests/white-list/forms/bing/BingDatingWhiteListForm.vue'
import TiktokGamblingWhiteListForm from '@shared-components/pages/requests/white-list/forms/tiktok/TiktokGamblingWhiteListForm.vue'
import TiktokCryptoWhiteListForm from '@shared-components/pages/requests/white-list/forms/tiktok/TiktokCryptoWhiteListForm.vue'

const {notify} = useNotification()

const {createAdAccountWhiteListRequest} = useAdAccountWhiteListRequestStore()
const {pingBackend} = useDefaultStore()
const {authUser, isCoreTeam, isPoint2WebEmployee} = storeToRefs(useAuthenticationStore())
const {companies} = storeToRefs(useCompanyStore())

// Props & emits
const emit = defineEmits(['created', 'hidden'])
const props = defineProps({
  activeRequest: {
    type: Object,
    default: null,
  },
  id: {
    type: String,
    default: null,
  },
})

// Refs
const modalId = ref(props.id ?? 'white-list-modal')
const modal = ref()
const loading = ref(false)
const error = ref(null)
const activeWhiteListRequest = ref()
const form = ref()
const metaPharmaForm = ref()
const metaCryptoForm = ref()
const metaGamblingForm = ref()
const metaDatingForm = ref()
const googleGamblingForm = ref()
const googleSocialCasinoForm = ref()
const googleFinancialForm = ref()
const googleDatingForm = ref()
const requestFormData = new FormData()

const defaultMainFormData = {
  platform: '',
  company_hash: authUser.value.company?.hash ?? null,
  vertical: '',
  ad_account_id: '',
  comment: '',
}

let mainFormData = ref(cloneSafe(defaultMainFormData))
let formDataFields = ref({})

// Computed
const companiesOptions = computed(() =>
  companies.value.map(company => ({
    value: company?.hash,
    label: company?.title,
  })),
)
const verticalOptions = computed(() => {
  if (mainFormData.value.platform === 'facebook') {
    return [
      {value: 'crypto', label: 'Crypto'},
      {value: 'gambling', label: 'Gambling'},
      {value: 'dating', label: 'Dating'},
      {value: 'pharma', label: 'Pharma'},
    ]
  } else if (mainFormData.value.platform === 'google') {
    return [
      {value: 'gambling', label: 'Online, Real-Money Gambling'},
      {value: 'social_casino', label: 'Social Casino'},
      {value: 'financial_products', label: 'Financial products and services'},
      {value: 'health_insurance', label: 'Health insurance (US only)'},
      {value: 'dating', label: 'Dating'},
    ]
  } else if (mainFormData.value.platform === 'bing') {
    return [
      {value: 'gambling', label: 'Gambling and Betting'},
      {value: 'crypto', label: 'Cryptocurrency-related products (includes trading)'},
      {value: 'dating', label: 'Dating'},
    ]
  } else if (mainFormData.value.platform === 'tiktok') {
    return [
      {value: 'gambling', label: 'Gambling and Betting'},
      {value: 'crypto', label: 'Financial Products & Crypto'},
    ]
  } else {
    return [
      {value: 'gambling', label: 'Gambling and Betting'},
      {value: 'financial_products', label: 'Financial Products & Crypto'},
      {value: 'pharma', label: 'Health-related Products'},
    ]
  }
})
const isSubmitButtonDisabled = computed(() => {
  const mainFieldsFilled =
    mainFormData.value.platform &&
    mainFormData.value.vertical &&
    mainFormData.value.company_hash &&
    mainFormData.value.ad_account_id

  return !mainFieldsFilled
})
let platfromOptions = [
  {value: 'facebook', label: 'Meta'},
  {value: 'google', label: 'Google'},
  {value: 'snapchat', label: 'Snapchat'},
  {value: 'bing', label: 'Microsoft Bing'},
  {value: 'tiktok', label: 'TikTok'},
]

// Methods
onMounted(() => {
  let modalElement = document.getElementById(modalId.value)
  modal.value = new BaseModal(modalElement)

  modalElement.addEventListener('hidden.bs.modal', () => {
    emit('hidden')
  })

  init()
  openModal()
})

const init = () => {
  props.activeRequest !== null ? fillFormFields(props.activeRequest) : resetForms()
}

const onPlatformChange = value => {
  resetForms()

  mainFormData.value.platform = value
}

const fillFormFields = request => {
  activeWhiteListRequest.value = cloneSafe(request)
  mainFormData.value = cloneSafe(request)
  mainFormData.value.company_hash = request.company?.hash
}

let resetForms = () => {
  activeWhiteListRequest.value = ''
  mainFormData.value = cloneSafe(defaultMainFormData)

  metaPharmaForm.value?.resetForm()
  metaCryptoForm.value?.resetForm()
  metaGamblingForm.value?.resetForm()
  metaDatingForm.value?.resetForm()
}

const openModal = () => {
  error.value = null
  modal.value.show()
}

const closeModal = () => {
  form.value.reset()

  modal.value.hide()
}

defineExpose({openModal, closeModal})

const handleCreate = async () => {
  loading.value = true

  let payload = getRequestPayload()

  try {
    await createAdAccountWhiteListRequest(requestFormData, payload)
    pingBackend()
  } catch ({data}) {
    return (error.value = data)
  } finally {
    loading.value = false
  }

  emit('created')
  closeModal()
  notify({type: 'success', text: 'Whitelist created'})
}

const getRequestFile = fileFormData => {
  for (const pair of fileFormData.entries()) {
    if (!requestFormData.get(pair[0])) {
      requestFormData.append(pair[0], pair[1])
    }
  }
}

const deleteRequestFile = file_name => {
  requestFormData.delete(`fields[${file_name}]`)
}

const getRequestPayload = () => {
  let payload
  let cloned = cloneSafe(mainFormData.value)
  payload = Object.assign(cloned, formDataFields.value)

  if (mainFormData.value.platform === 'facebook') {
    payload.ad_account_id = `act_${payload.ad_account_id}`
  }

  return payload
}
const setFormDataFields = updatedFields => {
  formDataFields.value = updatedFields
}
</script>

<template>
  <div class="modal fade" :id="modalId" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <template v-if="!!activeWhiteListRequest"> Whitelist Ad Account #{{ mainFormData?.hash }}</template>
            <template v-else>Whitelist Ad Account Request</template>
          </h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeModal" />
        </div>
        <base-form @on-submitted="handleCreate" ref="form">
          <div class="modal-body">
            <div class="form-group">
              <label for="wl-platform">Platform *</label>
              <div class="form-group">
                <the-select
                  :options="platfromOptions"
                  v-model="mainFormData.platform"
                  id="wl-platform"
                  @change="onPlatformChange"
                  :disabled="!!activeWhiteListRequest"
                  :allow_clear="false"
                  :can_deselect="false"
                  always-show-scroll
                  required
                />
              </div>
            </div>
            <template v-if="mainFormData.platform">
              <div class="form-group">
                <label for="wl-vertical">Vertical *</label>
                <div class="form-group">
                  <the-select
                    :options="verticalOptions"
                    v-model="mainFormData.vertical"
                    id="wl-vertical"
                    :disabled="!!activeWhiteListRequest"
                    :allow_clear="false"
                    :can_deselect="false"
                    always-show-scroll
                    required
                  />
                </div>
              </div>
            </template>
            <template v-if="mainFormData.vertical">
              <div class="form-group" v-if="isCoreTeam">
                <label for="wl-company">Company *</label>
                <div class="form-group">
                  <the-select
                    v-model="mainFormData.company_hash"
                    :options="companiesOptions"
                    :disabled="!!activeWhiteListRequest"
                    placeholder="Select a Company"
                    id="wl-company"
                    :allow_clear="false"
                    :can_deselect="false"
                    required
                  />
                </div>
              </div>
              <div class="form-group">
                <template v-if="!!activeWhiteListRequest && mainFormData?.ad_account?.title">
                  <label>Ad Account *</label>
                  <input
                    v-model="mainFormData.ad_account.title"
                    placeholder="Ad account"
                    class="form-control"
                    readonly
                  />
                </template>
                <template v-else>
                  <label for="wl-ad-account-id">Ad Account *</label>
                  <div class="input-group">
                    <the-select-ad-accounts
                      class="col"
                      id="wl-ad-account-id"
                      v-model="mainFormData.ad_account_id"
                      value-to-return="external_id"
                      :payload="{
                        company_hash: mainFormData.company_hash,
                        platform: mainFormData.platform === 'facebook' ? 'meta' : mainFormData.platform,
                      }"
                      :disabled="!!activeWhiteListRequest || mainFormData.company_hash === null"
                      required
                    ></the-select-ad-accounts>
                  </div>
                </template>
              </div>
            </template>

            <template v-if="mainFormData.platform === 'facebook'">
              <meta-dating-white-list-form
                ref="metaDatingForm"
                v-if="mainFormData.vertical === 'dating'"
                :active-request-fields="activeWhiteListRequest?.whitelist_dating_request"
                @changed="setFormDataFields"
                :disabled="!!activeWhiteListRequest"
              ></meta-dating-white-list-form>
              <meta-gambling-white-list-form
                ref="metaGamblingForm"
                v-if="mainFormData.vertical === 'gambling'"
                :active-request-fields="activeWhiteListRequest?.whitelist_gambling_request"
                @changed="setFormDataFields"
                @file-updated="getRequestFile"
                @delete-file="deleteRequestFile"
                :disabled="!!activeWhiteListRequest"
              ></meta-gambling-white-list-form>
              <meta-crypto-white-list-form
                ref="metaCryptoForm"
                v-if="mainFormData.vertical === 'crypto'"
                :active-request-fields="activeWhiteListRequest?.whitelist_crypto_request"
                @changed="setFormDataFields"
                @file-updated="getRequestFile"
                @delete-file="deleteRequestFile"
                :disabled="!!activeWhiteListRequest"
              ></meta-crypto-white-list-form>
              <meta-pharma-white-list-form
                ref="metaPharmaForm"
                v-if="mainFormData.vertical === 'pharma'"
                :active-request-fields="activeWhiteListRequest?.whitelist_pharma_request"
                @changed="setFormDataFields"
                :disabled="!!activeWhiteListRequest"
              ></meta-pharma-white-list-form>
            </template>
            <template v-else-if="mainFormData.platform === 'google'">
              <google-gambling-white-list-form
                ref="googleGamblingForm"
                v-if="mainFormData.vertical === 'gambling'"
                :active-request-fields="activeWhiteListRequest?.whitelist_gambling_google_request"
                @changed="setFormDataFields"
                @file-updated="getRequestFile"
                @delete-file="deleteRequestFile"
                :disabled="!!activeWhiteListRequest"
              >
              </google-gambling-white-list-form>
              <google-social-casino-white-list-form
                ref="googleSocialCasinoForm"
                v-if="mainFormData.vertical === 'social_casino'"
                :active-request-fields="activeWhiteListRequest?.whitelist_social_casino_request"
                @changed="setFormDataFields"
                @file-updated="getRequestFile"
                @delete-file="deleteRequestFile"
                :disabled="!!activeWhiteListRequest"
              ></google-social-casino-white-list-form>
              <google-financial-white-list-form
                ref="googleFinancialForm"
                v-if="mainFormData.vertical === 'financial_products'"
                :active-request-fields="activeWhiteListRequest?.whitelist_financial_products_request"
                @changed="setFormDataFields"
                @file-updated="getRequestFile"
                @delete-file="deleteRequestFile"
                :disabled="!!activeWhiteListRequest"
              >
              </google-financial-white-list-form>
              <google-health-insurance-white-list-form
                ref="googleHealthInsuranceForm"
                v-if="mainFormData.vertical === 'health_insurance'"
                :active-request-fields="activeWhiteListRequest?.whitelist_health_insurance_request"
                @changed="setFormDataFields"
                @file-updated="getRequestFile"
                :disabled="!!activeWhiteListRequest"
              >
              </google-health-insurance-white-list-form>
              <google-dating-white-list-form
                ref="googleDatingForm"
                v-if="mainFormData.vertical === 'dating'"
                :active-request-fields="activeWhiteListRequest?.whitelist_dating_google_request"
                @changed="setFormDataFields"
                :disabled="!!activeWhiteListRequest"
              />
            </template>
            <template v-else-if="mainFormData.platform === 'bing'">
              <bing-gambling-white-list-form
                v-if="mainFormData.vertical === 'gambling'"
                :active-request-fields="activeWhiteListRequest?.whitelist_gambling_bing_request"
                @changed="setFormDataFields"
                @file-updated="getRequestFile"
                @delete-file="deleteRequestFile"
                :disabled="!!activeWhiteListRequest"
              />
              <bing-crypto-white-list-form
                v-if="mainFormData.vertical === 'crypto'"
                :active-request-fields="activeWhiteListRequest?.whitelist_crypto_bing_request"
                @changed="setFormDataFields"
                :disabled="!!activeWhiteListRequest"
              />
              <template v-if="mainFormData.vertical === 'dating'">
                <bing-dating-white-list-form
                  :active-request-fields="activeWhiteListRequest?.whitelist_dating_bing_request"
                  @changed="setFormDataFields"
                  :disabled="!!activeWhiteListRequest"
                />
                <div class="form-group">
                  <label for="bing-dating-wl-additional-information">Additional Information</label>
                  <textarea
                    v-model="mainFormData.comment"
                    class="form-control"
                    id="bing-dating-wl-additional-information"
                    rows="3"
                    :disabled="!!activeWhiteListRequest"
                  />
                </div>
              </template>
            </template>
            <template v-else-if="mainFormData.platform === 'tiktok'">
              <tiktok-gambling-white-list-form
                v-if="mainFormData.vertical === 'gambling'"
                :active-request-fields="activeWhiteListRequest?.whitelist_gambling_tiktok_request"
                @changed="setFormDataFields"
                @file-updated="getRequestFile"
                @delete-file="deleteRequestFile"
                :disabled="!!activeWhiteListRequest"
              />
              <tiktok-crypto-white-list-form
                v-if="mainFormData.vertical === 'crypto'"
                :active-request-fields="activeWhiteListRequest?.whitelist_crypto_tiktok_request"
                @changed="setFormDataFields"
                :disabled="!!activeWhiteListRequest"
              />
            </template>
            <template v-else>
              <snapchat-gambling-white-list-form
                v-if="mainFormData.vertical === 'gambling'"
                :active-request-fields="activeWhiteListRequest?.whitelist_gambling_snapchat_request"
                @changed="setFormDataFields"
                :disabled="!!activeWhiteListRequest"
              />
              <snapchat-pharma-white-list-form
                v-if="mainFormData.vertical === 'pharma'"
                :active-request-fields="activeWhiteListRequest?.whitelist_pharma_snapchat_request"
                @changed="setFormDataFields"
                :disabled="!!activeWhiteListRequest"
              />
              <snapchat-financial-white-list-form
                v-if="mainFormData.vertical === 'financial_products'"
                :active-request-fields="activeWhiteListRequest?.whitelist_financial_products_snapchat_request"
                @changed="setFormDataFields"
                :disabled="!!activeWhiteListRequest"
              />
            </template>

            <div
              v-if="
                mainFormData.platform &&
                mainFormData.vertical &&
                isCoreTeam &&
                !(mainFormData.platform === 'bing' && mainFormData.vertical === 'dating')
              "
              class="form-group"
            >
              <label for="wl-comment">Internal Comment</label>

              <textarea
                v-model="mainFormData.comment"
                class="form-control"
                id="wl-comment"
                rows="2"
                :disabled="!!activeWhiteListRequest"
              />
            </div>

            <div class="row" v-if="isPoint2WebEmployee">
              <div class="col d-flex justify-content-end">
                <div class="d-flex align-items-center gap-1">
                  <process-owner-row>Katia Aleksandrova</process-owner-row>
                </div>
              </div>
            </div>

            <api-errors class="mt-3" v-if="error" :error="error"></api-errors>
          </div>
          <div class="modal-footer justify-content-end custom gap-4">
            <button type="button" class="btn btn-secondary" :disabled="loading" @click="closeModal">Close</button>
            <div>
              <button
                v-if="!activeWhiteListRequest"
                type="submit"
                class="btn btn-primary"
                :disabled="loading || isSubmitButtonDisabled"
              >
                Submit
              </button>
            </div>
          </div>
        </base-form>
      </div>
    </div>
  </div>
</template>
